import { UserFriendlyError } from "../../UserFriendlyError";
import config from "../../../config";
import { adminPanelFeatureToggle } from "../../../utils/feature-toggle-utils";

const MAX_FB_PAGE_ITERATION = 15;
const FACEBOOK_SDK_SCRIPT_ID = "facebook-jssdk";
const FACEBOOK_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const FACEBOOK_SDK_ERROR =
  "It looks like the Facebook pixel is not loaded, so we can't connect to Instagram Insights. Please check if you have an ad blocker enabled.";
let facebookSdkReadyPromise: Promise<void> | null = null;
let initializedFacebookAppId: string | null = null;

export interface FBAccount {
  platformUserId: string;
  first_name?: string;
  last_name?: string;
}

export interface FBPage {
  id: string;
  name: string;
  /** Absent on a Page listed from a connection handle (SKE-4810). */
  access_token?: string;
  instagram_business_account?: { id: string };
}

export interface FBPageExtended extends FBPage {
  name_with_location_descriptor: string;
  category: unknown;
  category_list: unknown;
  tasks: unknown;
}

export interface InstaAccount {
  id: string;
  ig_id?: string;
  /** Absent when Meta's discovery sent none (SKE-4810). */
  username?: string;
  name?: string;
  followers_count?: number;
  profile_picture_url?: string;
  website?: string;
}

export interface FBPageWithInsta {
  page: FBPage;
  ig?: InstaAccount;
}

/** The implicit grant: classic login, or Login for Business with a token. */
export interface FacebookTokenAuthResponse {
  accessToken: string;
  code?: undefined;
  [key: string]: unknown;
}

/**
 * SKE-4579. The code grant, when the login ran under the system-user Login
 * Configuration (admin toggle on AND config carries
 * `fbBusinessLoginSystemUserConfigId`). There is no token; finish the login
 * with `authorise-facebook.ts`.
 */
export interface FacebookCodeAuthResponse {
  code: string;
  accessToken?: undefined;
  [key: string]: unknown;
}

export type FacebookAuthResponse =
  FacebookTokenAuthResponse | FacebookCodeAuthResponse;

interface FacebookLoginOptions {
  scope?: string;
  return_scopes?: boolean;
  auth_type?: string;
  config_id?: string;
  response_type?: string;
  override_default_response_type?: boolean;
}

/**
 * SKE-5310. Which Login for Business configuration to use. `user` forces the
 * User-token configuration even where a System User configuration exists.
 */
export type BusinessTokenType = "system_user" | "user";

export interface FbLoginOptions {
  useBusinessLogin?: boolean;
  businessTokenType?: BusinessTokenType;
}

/**
 * The person closed or declined Facebook's dialog. Marked rather than
 * subclassed, so nothing extends `UserFriendlyError` at module load.
 */
export function createFacebookLoginCancelledError(): UserFriendlyError {
  const error = new UserFriendlyError(
    "No Instagram or Facebook account added: you cancelled the login or did not fully authorise our access."
  );
  return Object.assign(error, { facebookLoginCancelled: true });
}

export function isFacebookLoginCancelled(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { facebookLoginCancelled?: unknown }).facebookLoginCancelled ===
      true
  );
}

/**
 * SKE-5310. Whether a login may fall back to the User-token configuration:
 * Login for Business is on and this environment has a System User
 * configuration to fall back from. Classic logins never fall back.
 */
export function canFallBackToUserToken(options: FbLoginOptions = {}): boolean {
  return (
    options.businessTokenType !== "user" &&
    getFacebookSystemUserConfigId(options.useBusinessLogin ?? true) !==
      undefined
  );
}

function isFBSdkOk(): boolean {
  return (window as any).FB && (window as any).FB.login;
}

function createFacebookSdkError() {
  return new UserFriendlyError(FACEBOOK_SDK_ERROR);
}

function getFacebookAppId(useBusinessLogin = true) {
  return getFacebookAuthType(useBusinessLogin) === "business_login"
    ? config.fbBusinessLoginAppId
    : config.fbAppId;
}

function initFacebookSdk(appId: string) {
  window.FB.init({
    appId,
    autoLogAppEvents: true,
    xfbml: true,
    version: config.fbGraphApiVersion,
  });
  initializedFacebookAppId = appId;
}

async function ensureFacebookSdkReady(
  appId = initializedFacebookAppId || config.fbAppId
): Promise<void> {
  if (isFBSdkOk()) {
    if (initializedFacebookAppId !== appId) {
      initFacebookSdk(appId);
    }
    return;
  }

  if (!appId) {
    throw createFacebookSdkError();
  }

  if (!facebookSdkReadyPromise) {
    facebookSdkReadyPromise = new Promise<void>((resolve, reject) => {
      const previousFbAsyncInit = window.fbAsyncInit;
      const fail = () => reject(createFacebookSdkError());
      const finalize = () => {
        if (isFBSdkOk()) {
          resolve();
        } else {
          fail();
        }
      };

      window.fbAsyncInit = () => {
        try {
          previousFbAsyncInit?.();

          if (!window.FB?.init) {
            fail();
            return;
          }

          initFacebookSdk(appId);
          finalize();
        } catch {
          fail();
        }
      };

      const existingScript = document.getElementById(FACEBOOK_SDK_SCRIPT_ID);

      if (!existingScript) {
        const script = document.createElement("script");
        script.id = FACEBOOK_SDK_SCRIPT_ID;
        script.async = true;
        script.src = FACEBOOK_SDK_SRC;
        script.onerror = fail;
        document.head.append(script);
      }

      window.setTimeout(() => {
        if (!isFBSdkOk()) {
          fail();
        }
      }, 10_000);
    }).finally(() => {
      if (!isFBSdkOk()) {
        facebookSdkReadyPromise = null;
      }
    });
  }

  await facebookSdkReadyPromise;
}

export function getFacebookAuthType(useBusinessLogin = true) {
  return useBusinessLogin &&
    config.fbBusinessLoginAppId &&
    config.fbBusinessLoginConfigId &&
    adminPanelFeatureToggle.syncGet("enableFacebookLoginForBusiness")
    ? "business_login"
    : "classic";
}

/**
 * SKE-4579. The system-user Login Configuration id, when the code grant may be
 * used: the admin is on Facebook Login for Business AND this environment's
 * config carries the id. Production config has no such key, so this is always
 * undefined there.
 */
export function getFacebookSystemUserConfigId(
  useBusinessLogin = true
): string | undefined {
  return getFacebookAuthType(useBusinessLogin) === "business_login"
    ? config.fbBusinessLoginSystemUserConfigId || undefined
    : undefined;
}

export async function fbLogin(
  scopes: string[],
  options: FbLoginOptions = {}
): Promise<FacebookAuthResponse> {
  const useBusinessLogin = options.useBusinessLogin ?? true;
  const appId = getFacebookAppId(useBusinessLogin);

  if (!appId) {
    throw createFacebookSdkError();
  }

  await ensureFacebookSdkReady(appId);
  return new Promise((resolve, reject) => {
    if (!isFBSdkOk()) {
      return reject(createFacebookSdkError());
    }
    let loginOptions: FacebookLoginOptions = {
      scope: scopes.join(", "),
      return_scopes: true,
      auth_type: "rerequest",
    };
    const systemUserConfigId =
      options.businessTokenType === "user"
        ? undefined
        : getFacebookSystemUserConfigId(useBusinessLogin);
    if (systemUserConfigId) {
      // SKE-4579. Exactly Meta's documented system-user call: the Login
      // Configuration replaces `scope`, and sending one fails the dialog.
      loginOptions = {
        config_id: systemUserConfigId,
        response_type: "code",
        override_default_response_type: true,
      };
    } else if (getFacebookAuthType(useBusinessLogin) === "business_login") {
      // The Login Configuration replaces `scope`; a scope the business app has
      // not added fails the dialog for app-role users.
      delete loginOptions.scope;
      loginOptions.config_id = config.fbBusinessLoginConfigId;
      loginOptions.response_type = "token";
      loginOptions.override_default_response_type = true;
    }
    if (loginOptions.scope) console.log("Requesting scopes: " + scopes);
    window.FB.login(
      (response: { authResponse?: FacebookAuthResponse | null }) => {
        if (!response.authResponse) {
          console.log("User cancelled login or did not fully authorize.");
          reject(createFacebookLoginCancelledError());
        } else {
          resolve(response.authResponse);
        }
      },
      loginOptions
    );
  });
}

export async function fbReadMe(auth: FacebookTokenAuthResponse) {
  await ensureFacebookSdkReady();
  return new Promise<FBAccount>((resolve, reject) => {
    if (!isFBSdkOk()) {
      return reject(createFacebookSdkError());
    }
    window.FB.api("/me", { accessToken: auth.accessToken }, (response: any) => {
      if (!response || response.error) {
        return reject(response?.error);
      }
      const account: FBAccount = {
        platformUserId: response.id,
      };
      if (response.name) {
        const [first_name, last_name] = response.name.split(" ");
        account.first_name = first_name;
        account.last_name = last_name;
      }
      resolve(account);
    });
  });
}

const readFbPagePromise = (
  accessToken: string,
  pageUrl: string,
  useShortPages: boolean
) =>
  new Promise((resolve) => {
    window.FB.api(
      pageUrl,
      { accessToken, limit: useShortPages ? 15 : 100 },
      (response: any) => {
        console.log(`${pageUrl} response`, response);
        return resolve(response);
      }
    );
  });

const fbReadPagesCore = async (
  auth: FacebookTokenAuthResponse,
  pageUrl: string,
  useShortPages?: boolean
): Promise<any[]> => {
  await ensureFacebookSdkReady();
  if (!isFBSdkOk()) {
    throw createFacebookSdkError();
  }

  let nextPageUrl = pageUrl;
  let result: Array<any> = [];

  for (
    let i = 0;
    i < (useShortPages ? MAX_FB_PAGE_ITERATION * 2 : MAX_FB_PAGE_ITERATION);
    i++
  ) {
    const response: any = await readFbPagePromise(
      auth.accessToken,
      nextPageUrl,
      useShortPages || false
    );

    if (!response || response.error) {
      throw new Error(response?.error);
    }

    result = [...result, ...response.data];
    if (response.paging?.next) {
      nextPageUrl = response.paging.next;
    } else {
      break;
    }
  }
  return result;
};

export async function fbReadPages(
  auth: FacebookTokenAuthResponse,
  useShortPages: boolean
): Promise<FBPage[]> {
  return await fbReadPagesCore(
    auth,
    "/me/accounts?fields=id,name,access_token,instagram_business_account",
    useShortPages
  );
}

export async function fbReadPagesExtended(
  auth: FacebookTokenAuthResponse,
  useShortPages: boolean
): Promise<FBPageExtended[]> {
  return await fbReadPagesCore(
    auth,
    "/me/accounts?fields=id,name,name_with_location_descriptor,access_token,category,category_list,tasks",
    useShortPages
  );
}

export async function fbReadInstaBizAccount(
  page: FBPage,
  auth: FacebookTokenAuthResponse
): Promise<FBPageWithInsta> {
  await ensureFacebookSdkReady();
  return new Promise((resolve, reject) => {
    if (!isFBSdkOk()) {
      return reject(createFacebookSdkError());
    }
    if (!page.instagram_business_account) {
      return resolve({ page });
    }
    window.FB.api(
      `/${
        page.instagram_business_account!.id
      }?fields=id,ig_id,followers_count,profile_picture_url,name,username,website`,
      { accessToken: auth.accessToken },
      (response: any) => {
        if (!response || response.error) {
          return reject(response?.error);
        }
        console.log("fbData Becomes igAccountData", response);
        // NOTE: this is the data we collect
        resolve({ page, ig: response });
      }
    );
  });
}
